/**
 * 스토리지 설정 관리자 페이지
 * Firebase Storage 단독 / NAS 하이브리드 모드 선택 및 설정
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  InputLabel,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Storage,
  Cloud,
  CheckCircle,
  Error as ErrorIcon,
  Refresh
} from '@mui/icons-material';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase.config';
import { useAuth } from '../../context/AuthContext';
import {
  StorageConfig,
  StorageMode,
  NasProvider,
  ConnectionType,
  DEFAULT_STORAGE_RULES,
  DEFAULT_NAS_CONFIG
} from '../../types/storage-config.types';

/**
 * 폼 상태 인터페이스
 */
interface FormState extends StorageConfig {
  showPassword: boolean;
}

const StorageSettings: React.FC = () => {
  const { user } = useAuth();
  
  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // 폼 상태
  const [mode, setMode] = useState<StorageMode>('firebase-only');
  const [nasEnabled, setNasEnabled] = useState(false);
  const [nasProvider, setNasProvider] = useState<NasProvider>('synology');
  const [connectionType, setConnectionType] = useState<ConnectionType>('webdav');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(5006);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sharedFolder, setSharedFolder] = useState('egmms_files');
  
  // 저장 규칙
  const [sizeThreshold, setSizeThreshold] = useState(10);
  const [autoFolderStructure, setAutoFolderStructure] = useState(true);
  const [createInfoFiles, setCreateInfoFiles] = useState(true);
  
  // UI 상태
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [testMessage, setTestMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  /**
   * 초기 로드: Firestore에서 설정 불러오기
   */
  useEffect(() => {
    loadStorageConfig();
  }, []);

  const loadStorageConfig = async () => {
    try {
      setLoading(true);
      const configRef = doc(db, 'system', 'storageConfig');
      const configSnap = await getDoc(configRef);
      
      if (configSnap.exists()) {
        const data = configSnap.data() as StorageConfig;
        
        // 모드 설정
        setMode(data.mode);
        
        // NAS 설정
        if (data.nas) {
          setNasEnabled(data.nas.enabled);
          setNasProvider(data.nas.provider);
          setConnectionType(data.nas.connectionType);
          setHost(data.nas.host);
          setPort(data.nas.port);
          setUsername(data.nas.username);
          setPassword(data.nas.password);
          setSharedFolder(data.nas.sharedFolder);
        }
        
        // 저장 규칙 (없으면 기본값 사용)
        if (data.rules) {
          setSizeThreshold(data.rules.sizeThreshold);
          setAutoFolderStructure(data.rules.nasAutoFolderStructure);
          setCreateInfoFiles(data.rules.nasCreateInfoFiles);
        }
      }
    } catch (error) {
      console.error('설정 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 설정 저장
   */
  const handleSave = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      
      const config: StorageConfig = {
        mode,
        updatedAt: Timestamp.now(),
        updatedBy: user.uid,
        rules: {
          sizeThreshold,
          nasAutoFolderStructure: autoFolderStructure,
          nasCreateInfoFiles: createInfoFiles,
          firebaseFiles: DEFAULT_STORAGE_RULES.firebaseFiles,
          nasFiles: DEFAULT_STORAGE_RULES.nasFiles
        }
      };
      
      // 하이브리드 모드일 경우 NAS 설정 추가
      if (mode === 'hybrid') {
        config.nas = {
          enabled: nasEnabled,
          provider: nasProvider,
          connectionType,
          host,
          port,
          username,
          password,
          sharedFolder,
          status: 'disconnected',
          lastTested: Timestamp.now()
        };
      }
      
      // Firestore에 저장
      const configRef = doc(db, 'system', 'storageConfig');
      await setDoc(configRef, config);
      
      setSnackbarMessage('설정이 저장되었습니다.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('저장 실패:', error);
      const errorMessage = error?.message || '알 수 없는 오류';
      setSnackbarMessage(`저장에 실패했습니다: ${errorMessage}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  /**
   * NAS 연결 테스트
   * TODO: Phase 3에서 실제 프록시 서버 연결 구현
   */
  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setTestMessage('');
    
    try {
      // 기본 검증
      if (!host || !username || !password) {
        throw new Error('필수 정보를 모두 입력해주세요.');
      }
      
      // TODO: Phase 3 - 실제 프록시 서버 API 호출
      // const response = await fetch(`${PROXY_URL}/api/test-connection`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ host, port, username, password, sharedFolder })
      // });
      
      // 임시: 기본 검증만 수행
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setTestResult('success');
      setTestMessage('연결 테스트 성공! (Phase 3에서 실제 NAS 연결 구현 예정)');
    } catch (error: any) {
      setTestResult('error');
      setTestMessage(error.message || '연결 테스트 실패');
    } finally {
      setTesting(false);
    }
  };

  // 로딩 중
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        {/* 헤더 */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Storage sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              스토리지 설정
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            파일 저장 방식을 선택하고 NAS 연결을 설정합니다.
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* 스토리지 모드 선택 */}
        <Box sx={{ mb: 4 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
              스토리지 모드 선택
            </FormLabel>
            <RadioGroup
              value={mode}
              onChange={(e) => setMode(e.target.value as StorageMode)}
            >
              <FormControlLabel
                value="firebase-only"
                control={<Radio />}
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Cloud sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body1" fontWeight="bold">
                        Firebase Storage만 사용
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      소규모 기업에 적합. 모든 파일을 Firebase에 저장합니다.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="hybrid"
                control={<Radio />}
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Storage sx={{ mr: 1, color: 'success.main' }} />
                      <Typography variant="body1" fontWeight="bold">
                        Firebase + NAS 하이브리드
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      대규모 기업에 적합. 대용량 파일은 NAS, 작은 파일은 Firebase에 저장합니다.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* NAS 설정 (하이브리드 모드일 때만 표시) */}
        {mode === 'hybrid' && (
          <Box sx={{ mb: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              🔧 NAS 연결 설정
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {/* NAS 제조사 */}
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                <FormControl fullWidth>
                  <InputLabel>NAS 제조사</InputLabel>
                  <Select
                    value={nasProvider}
                    label="NAS 제조사"
                    onChange={(e) => setNasProvider(e.target.value as NasProvider)}
                  >
                    <MenuItem value="synology">Synology</MenuItem>
                    <MenuItem value="qnap">QNAP</MenuItem>
                    <MenuItem value="other">기타</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* 연결 타입 */}
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                <FormControl fullWidth>
                  <InputLabel>연결 타입</InputLabel>
                  <Select
                    value={connectionType}
                    label="연결 타입"
                    onChange={(e) => setConnectionType(e.target.value as ConnectionType)}
                  >
                    <MenuItem value="webdav">WebDAV (권장)</MenuItem>
                    <MenuItem value="smb">SMB</MenuItem>
                    <MenuItem value="ftp">FTP</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* DDNS 주소 */}
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '65%' } }}>
                <TextField
                  fullWidth
                  label="DDNS 주소"
                  placeholder="yournas.synology.me"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  helperText="NAS의 DDNS 주소 또는 IP를 입력하세요"
                />
              </Box>

              {/* 포트 */}
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '30%' } }}>
                <TextField
                  fullWidth
                  label="포트"
                  type="number"
                  value={port}
                  onChange={(e) => setPort(Number(e.target.value))}
                  helperText="WebDAV 기본: 5006"
                />
              </Box>

              {/* 계정 */}
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                <TextField
                  fullWidth
                  label="계정"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  helperText="NAS 접속 계정"
                />
              </Box>

              {/* 비밀번호 */}
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label="비밀번호"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* 공유 폴더 */}
              <Box sx={{ flex: '1 1 auto', minWidth: '100%' }}>
                <TextField
                  fullWidth
                  label="공유 폴더명"
                  value={sharedFolder}
                  onChange={(e) => setSharedFolder(e.target.value)}
                  helperText="NAS에서 생성한 공유 폴더명 (예: egmms_files)"
                />
              </Box>

              {/* 연결 테스트 */}
              <Box sx={{ flex: '1 1 auto', minWidth: '100%' }}>
                <Button
                  variant="outlined"
                  onClick={handleTestConnection}
                  disabled={testing || !host || !username || !password}
                  startIcon={testing ? <CircularProgress size={20} /> : <Refresh />}
                  fullWidth
                  sx={{ height: 48 }}
                >
                  {testing ? '연결 테스트 중...' : '연결 테스트'}
                </Button>
              </Box>

              {/* 테스트 결과 */}
              {testResult && (
                <Box sx={{ flex: '1 1 auto', minWidth: '100%' }}>
                  <Alert 
                    severity={testResult} 
                    icon={testResult === 'success' ? <CheckCircle /> : <ErrorIcon />}
                  >
                    {testMessage}
                  </Alert>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* 저장 규칙 */}
        <Box sx={{ mb: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
            📊 저장 규칙 설정
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {/* 크기 임계값 */}
            <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
              <TextField
                fullWidth
                label="크기 임계값 (MB)"
                type="number"
                value={sizeThreshold}
                onChange={(e) => setSizeThreshold(Number(e.target.value))}
                helperText={
                  mode === 'hybrid' 
                    ? `${sizeThreshold}MB 이상 파일은 NAS에 저장됩니다.`
                    : '하이브리드 모드에서만 적용됩니다.'
                }
                disabled={mode === 'firebase-only'}
              />
            </Box>

            {/* 자동 폴더 구조 */}
            <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
              <FormControlLabel
                control={
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                    <input
                      type="checkbox"
                      checked={autoFolderStructure}
                      onChange={(e) => setAutoFolderStructure(e.target.checked)}
                      disabled={mode === 'firebase-only'}
                      style={{ width: 20, height: 20, cursor: 'pointer' }}
                    />
                  </Box>
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1">폴더 구조 자동 생성</Typography>
                    <Typography variant="caption" color="text.secondary">
                      프로젝트/금형 생성 시 NAS에 폴더 자동 생성
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* 정보 파일 생성 */}
            <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
              <FormControlLabel
                control={
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                    <input
                      type="checkbox"
                      checked={createInfoFiles}
                      onChange={(e) => setCreateInfoFiles(e.target.checked)}
                      disabled={mode === 'firebase-only'}
                      style={{ width: 20, height: 20, cursor: 'pointer' }}
                    />
                  </Box>
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1">정보 파일 자동 생성</Typography>
                    <Typography variant="caption" color="text.secondary">
                      _info.txt 파일을 각 폴더에 자동 생성
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* 저장 버튼 */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={loadStorageConfig}
            disabled={saving}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving && <CircularProgress size={20} />}
          >
            {saving ? '저장 중...' : '설정 저장'}
          </Button>
        </Box>

        {/* 스낵바 */}
        {snackbarOpen && (
          <Alert 
            severity={snackbarSeverity}
            sx={{ mt: 2 }}
            onClose={() => setSnackbarOpen(false)}
          >
            {snackbarMessage}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default StorageSettings;
